import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { EntityThumbnail } from '../components/EntityImage';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Heart, Plus, X, ExternalLink, DollarSign } from 'lucide-react';

const WISHLIST_CATEGORIES = ['Firearm', 'Ammo', 'Accessory'];
const PRIORITIES = ['Low', 'Medium', 'High'];

export default function WishlistPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');

  const fetchWishlist = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (categoryFilter) params.append('category', categoryFilter);
      if (priorityFilter) params.append('priority', priorityFilter);

      const response = await api.get(`/wishlist?${params.toString()}`);
      setItems(response.data);
    } catch (error) {
      console.error('Error fetching wishlist:', error);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, categoryFilter, priorityFilter]);

  useEffect(() => {
    fetchWishlist();
  }, [fetchWishlist]);

  const getPriorityColor = (priority) => {
    const colors = {
      'High': 'bg-destructive/20 text-destructive border-destructive/30',
      'Medium': 'bg-accent/20 text-accent border-accent/30',
      'Low': 'bg-muted text-muted-foreground border-muted',
    };
    return colors[priority] || colors['Medium'];
  };

  const clearFilters = () => {
    setCategoryFilter('');
    setPriorityFilter('');
    setSearchQuery('');
  };

  const hasActiveFilters = categoryFilter || priorityFilter;

  return (
    <Layout
      title="Wishlist"
      showSearch
      onSearch={setSearchQuery}
      searchPlaceholder="Search wishlist..."
    >
      <div className="space-y-6" data-testid="wishlist-page">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-36 rounded-sm" data-testid="filter-category">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {WISHLIST_CATEGORIES.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={priorityFilter} onValueChange={setPriorityFilter}>
              <SelectTrigger className="w-36 rounded-sm" data-testid="filter-priority">
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priorities</SelectItem>
                {PRIORITIES.map(p => (
                  <SelectItem key={p} value={p}>{p}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-muted-foreground"
              >
                <X className="w-4 h-4 mr-1" />
                Clear
              </Button>
            )}
          </div>

          <Button
            onClick={() => navigate('/wishlist/new')}
            className="bg-destructive hover:bg-destructive/90 rounded-sm btn-tactical"
            data-testid="add-wishlist-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add to Wishlist
          </Button>
        </div>

        {/* Content */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="bg-card border border-border rounded-sm p-4 animate-pulse">
                <div className="h-6 bg-muted rounded mb-2 w-3/4" />
                <div className="h-4 bg-muted rounded w-1/2" />
              </div>
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Heart className="w-16 h-16 text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Wishlist Items</h3>
            <p className="text-muted-foreground mb-6 max-w-md">
              {hasActiveFilters || searchQuery
                ? 'No items match your filters.'
                : 'Start tracking items you want to acquire.'}
            </p>
            {!hasActiveFilters && !searchQuery && (
              <Button
                onClick={() => navigate('/wishlist/new')}
                className="bg-destructive hover:bg-destructive/90 rounded-sm btn-tactical"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Item
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {items.map((item, index) => (
              <div
                key={item.id}
                className="bg-card border border-border rounded-sm overflow-hidden cursor-pointer card-hover animate-fade-in"
                style={{ animationDelay: `${index * 0.05}s` }}
                onClick={() => navigate(`/wishlist/${item.id}`)}
                data-testid={`wishlist-card-${item.id}`}
              >
                {/* Image - Using EntityThumbnail for object URL rendering */}
                <EntityThumbnail
                  imageFileIds={item.images}
                  alt={item.name}
                  containerClassName="aspect-[4/3] bg-muted overflow-hidden"
                  className="w-full h-full object-cover"
                  fallback={
                    <div className="w-full h-full flex items-center justify-center">
                      <Heart className="w-12 h-12 text-muted-foreground/30" />
                    </div>
                  }
                />

                {/* Content */}
                <div className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <Badge variant="outline" className={`rounded-sm ${getPriorityColor(item.priority)}`}>
                      {item.priority}
                    </Badge>
                    <span className="text-xs text-muted-foreground">{item.category}</span>
                  </div>

                  <h3 className="font-semibold text-foreground mb-1 truncate">{item.name}</h3>
                  
                  {item.target_price && (
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      <DollarSign className="w-4 h-4" />
                      {item.target_price.toLocaleString()}
                    </p>
                  )}

                  {item.url && (
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center gap-1 text-xs text-[#C9A24D] hover:text-[#D4B05E] hover:underline mt-2"
                    >
                      <ExternalLink className="w-3 h-3" />
                      View Link
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
